import React, {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useRef,
  useState
} from 'react';
import {
  PurchaseOrderItemFormHandle,
  SelectOption
} from './CreatePurchaseOrderForm';
import { PurchaseOrderItem } from './AddPurchaseOrderItemForm';
import axiosInstance from 'utils/axiosInterceptors';
import DialogBox, { ModalHandle } from './DialogBox';
import { CustomModalProps } from 'components/modals/CustomModal';
import { toast } from 'react-toastify';
import { toastConfig } from 'utils/toastConfig';
import * as yup from 'yup';
import { useBranchContext } from 'providers/BranchProvider';
import { AxiosError } from 'axios';
import { Col, Form, Row } from 'react-bootstrap';
import ReactSelect from 'components/base/ReactSelect';
import { unitOptions } from 'utils/unitOptions';
import { ActionMeta } from 'react-select';
import { unitOfMeasureMapping } from 'utils/unitOptions';
import { useProductTypeahead } from './useProductTypeahead';

export interface EditPurchaseOrderItemFormProps extends PurchaseOrderItem {
  purchaseOrderId: number | undefined;
  purchaseOrderItemId: number | undefined;
  onCommitted?: (result: {
    queuedProductMatch: boolean;
    purchaseOrderItemId: number | undefined;
  }) => void | Promise<void>;
}

const EditPurchaseOrderItemForm = forwardRef<
  PurchaseOrderItemFormHandle,
  EditPurchaseOrderItemFormProps
>(
  (
    {
      productId,
      productName,
      quantity,
      unitOfMeasure,
      remarks,
      purchaseOrderId,
      purchaseOrderItemId,
      onCommitted
    },
    ref
  ) => {
    const { selectedBranch } = useBranchContext();
    const [purchaseOrderItem, setPurchaseOrderItem] =
      useState<PurchaseOrderItem>({
        productId: productId,
        productName: productName,
        quantity: quantity,
        unitOfMeasure: unitOfMeasure,
        remarks: remarks,
        allowRecentlyOrdered: false,
        allowNonCriticalQuantity: false,
        allowUnsupportedBySupplier: false
      });
    const { options, isLoading, handleSearchChange, noOptionsMessage } =
      useProductTypeahead();
    const [modalProps, setModalProps] = useState<CustomModalProps>();
    const modalRef = useRef<ModalHandle>({
      show: () => {},
      hide: () => {}
    });
    const [defaultOption, setDefaultOption] = useState<SelectOption | null>(
      productId
        ? { value: productId, label: productName || 'Selected product' }
        : null
    );

    const validateProductName = () => {
      const isUserInput = purchaseOrderItem.productName !== productName;

      if (purchaseOrderItem.productName === '') {
        return false;
      }

      if (
        !defaultOption?.label.includes(productName as string) &&
        isUserInput
      ) {
        return true;
      }

      return false;
    };

    const handleFormChange = <K extends keyof PurchaseOrderItem>(
      field: K,
      value: PurchaseOrderItem[K]
    ) => {
      setPurchaseOrderItem(prev => ({ ...prev, [field]: value }));
    };

    const handleChange = (
      newValue: unknown,
      actionMeta: ActionMeta<unknown>
    ) => {
      if (actionMeta.action === 'select-option') {
        const selectedOption = newValue as SelectOption;
        setDefaultOption(selectedOption);
        handleFormChange('productId', selectedOption.value);
        handleFormChange('productName', '');
      } else if (actionMeta.action === 'clear') {
        setDefaultOption(null);
        handleFormChange('productId', null);
        handleFormChange('productName', '');
      }
    };

    const persistItem = async (
      item: PurchaseOrderItem,
      notifyParent: boolean
    ) => {
      try {
        await purchaseOrderItemSchema.validate(item, {
          abortEarly: false
        });

        await axiosInstance.put(
          `/api/v1/${selectedBranch?.branchDomain}/purchaseOrders/${purchaseOrderId}/items/${purchaseOrderItemId}`,
          item
        );
        toast.success('Item edited successfully', {
          ...toastConfig,
          autoClose: 2000
        });
        const queuedProductMatch =
          item.productId == null && !!item.productName?.trim();
        if (notifyParent) {
          await onCommitted?.({
            queuedProductMatch,
            purchaseOrderItemId
          });
        }
        return {
          success: true,
          queuedProductMatch,
          purchaseOrderItemId
        };
      } catch (error) {
        if (error instanceof yup.ValidationError) {
          error.inner.forEach(err => {
            toast.error(err.message, { ...toastConfig, autoClose: 3000 });
          });
        } else if (error instanceof AxiosError) {
          if (
            error.response?.data.errorMessage &&
            error.response?.status === 400
          ) {
            modalRef.current.hide();
            errorMessageCatch(error.response?.data.errorMessage, item);
          } else {
            toast.error(
              error.response?.data?.errorMessage || 'Failed to edit item',
              { ...toastConfig, autoClose: 3000 }
            );
          }
        }
        return false;
      }
    };

    const confirmAndResubmit = (
      item: PurchaseOrderItem,
      overrides: Partial<PurchaseOrderItem>
    ) => {
      const next = { ...item, ...overrides };
      setPurchaseOrderItem(next);
      modalRef.current.hide();
      void persistItem(next, true);
    };

    const errorMessageCatch = (errorMessage: string, item: PurchaseOrderItem) => {
      if (errorMessage.includes('Product still has')) {
        modalRef.current.show();
        setModalProps({
          title: 'Warning',
          content: <p>{errorMessage}</p>,
          onSubmit: () =>
            confirmAndResubmit(item, { allowNonCriticalQuantity: true }),
          onCancel: () => modalRef.current.hide()
        });
      } else if (
        errorMessage.includes(
          'Product is not yet supported by this supplier, Do you want to continue?'
        )
      ) {
        modalRef.current.show();
        setModalProps({
          title: 'Warning',
          content: <p>{errorMessage}</p>,
          onSubmit: () =>
            confirmAndResubmit(item, { allowUnsupportedBySupplier: true }),
          onCancel: () => modalRef.current.hide()
        });
      } else if (errorMessage.includes('Product is ordered recently')) {
        modalRef.current.show();
        setModalProps({
          title: 'Warning',
          content: <p>{errorMessage}</p>,
          onSubmit: () =>
            confirmAndResubmit(item, { allowRecentlyOrdered: true }),
          onCancel: () => modalRef.current.hide()
        });
      } else {
        toast.error(errorMessage, { ...toastConfig, autoClose: 3000 });
      }
    };

    const handleSubmit = () => persistItem(purchaseOrderItem, false);

    const purchaseOrderItemSchema = yup.object({
      productId: yup.number().nullable().optional(),
      productName: yup
        .string()
        .nullable()
        .when('productId', {
          is: (productId: number | null) => productId === null,
          then: schema =>
            schema.required(
              'Product name is required when product ID is not provided'
            ),
          otherwise: schema => schema.optional()
        }),
      quantity: yup
        .number()
        .required('Quantity is required')
        .min(1, 'Quantity must be greater than or equal to 1'),
      unitOfMeasure: yup.string().default('Piece'),
      remarks: yup.string().nullable().optional()
    });

    useImperativeHandle(ref, () => ({
      submit: handleSubmit
    }));

    useEffect(() => {
      const enumValue = unitOfMeasureMapping[unitOfMeasure];
      handleFormChange('unitOfMeasure', enumValue);
    }, [unitOfMeasure]);

    return (
      <>
        <Form className="w-100">
          <Row className="mb-3">
            <Form.Group as={Col}>
              <Form.Label>Product ID</Form.Label>
              <ReactSelect
                value={defaultOption}
                placeholder="Type at least 2 characters"
                options={options}
                onChange={handleChange}
                onInputChange={handleSearchChange}
                filterOption={null}
                noOptionsMessage={noOptionsMessage}
                isLoading={isLoading}
                closeMenuOnSelect={true}
                isSearchable
                isClearable={true}
                isDisabled={validateProductName()}
              />
            </Form.Group>
          </Row>
          <Row className="mb-3">
            <Form.Group as={Col}>
              <Form.Label>Product Name</Form.Label>
              <Form.Control
                type="text"
                value={purchaseOrderItem.productName || ''}
                onChange={e => {
                  handleFormChange('productName', e.target.value);
                }}
                disabled={purchaseOrderItem.productId !== null}
              />
            </Form.Group>
          </Row>
          <Row className="mb-3">
            <Form.Group as={Col}>
              <Form.Label>Quantity</Form.Label>
              <Form.Control
                type="number"
                value={purchaseOrderItem.quantity || ''}
                onChange={e => {
                  handleFormChange('quantity', +e.target.value);
                }}
              />
            </Form.Group>
          </Row>
          <Row className="mb-3">
            <Form.Group as={Col}>
              <Form.Label>Unit of Measure</Form.Label>
              <Form.Select
                value={purchaseOrderItem.unitOfMeasure || ''}
                onChange={e => {
                  handleFormChange('unitOfMeasure', e.target.value);
                }}
              >
                {unitOptions.map(option => (
                  <option key={option.label} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </Form.Select>
            </Form.Group>
          </Row>
          <Row className="mb-3">
            <Form.Group as={Col}>
              <Form.Label>Remarks</Form.Label>
              <Form.Control
                type="text"
                value={purchaseOrderItem.remarks || ''}
                onChange={e => {
                  handleFormChange('remarks', e.target.value);
                }}
              />
            </Form.Group>
          </Row>
        </Form>
        <DialogBox
          ref={modalRef}
          title={modalProps?.title}
          content={modalProps?.content}
          onCancel={modalProps?.onCancel}
          onSubmit={modalProps?.onSubmit}
          onSubmitCustomDisplay="Yes"
          onCancelCustomDisplay="No"
        />
      </>
    );
  }
);

export default EditPurchaseOrderItemForm;
